/**
 * P2-S5 마무리 스크립트(SR-TCH-03 · FS-13) — 읽기 · 기본 템플릿(모양은 ../types.ts).
 *  - 상태(ScriptState)는 제어 평면이 갖고(교사 POST …/teacher/room/script), 룸 서버가 학생에게 알린다(meta.script · ev script).
 *  - 단계 시간이 끝나도 자동으로 넘기지 않는다 — 교사가 '다음 단계'(남은 0 이면 버튼만 강조).
 *  - 기본 템플릿(10분 기준)은 제어 평면이 없을 때(가짜 · 미리보기)만 쓴다 — 실제 초는 서버가 활동 시간에 비례해 준다.
 */

import type { ScriptTemplateKey, ScriptStep, ScriptState, ScriptTemplate } from "../types";

export const SCRIPT_TEMPLATE_KEYS: readonly ScriptTemplateKey[] = ["view", "challenge", "team"];

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

export const isTemplateKey = (v: unknown): v is ScriptTemplateKey =>
  typeof v === "string" && (SCRIPT_TEMPLATE_KEYS as readonly string[]).includes(v);

export function readSteps(v: unknown): ScriptStep[] {
  return (Array.isArray(v) ? v : [])
    .map(obj)
    .filter((s): s is Record<string, unknown> => !!s && typeof s.key === "string")
    .map((s) => ({
      key: String(s.key),
      icon: typeof s.icon === "string" ? s.icon : "•",
      seconds: num(s.seconds) ?? 60,
      modeId: typeof s.modeId === "string" ? s.modeId : "lobby",
    }));
}

/** 서버 모양 → ScriptState(모르는 모양이면 null) */
export function readScript(v: unknown): ScriptState | null {
  const o = obj(v);
  if (!o || !isTemplateKey(o.template)) return null;
  const steps = readSteps(o.steps);
  if (!steps.length) return null;
  const index = Math.max(0, Math.min(steps.length - 1, Math.floor(num(o.index) ?? 0)));
  return {
    template: o.template,
    steps,
    index,
    remainingS: num(o.remainingS) ?? steps[index]!.seconds,
    running: o.running !== false,
    done: o.done === true,
  };
}

export function readTemplates(v: unknown): ScriptTemplate[] {
  return (Array.isArray(v) ? v : [])
    .map(obj)
    .filter((t): t is Record<string, unknown> => !!t && isTemplateKey(t.key))
    .map((t) => ({ key: t.key as ScriptTemplateKey, steps: readSteps(t.steps) }))
    .filter((t) => t.steps.length > 0);
}

/** 기본 템플릿(계약 §0 — 10분 기준 · 분) */
const BASE: Record<ScriptTemplateKey, readonly [string, string, string, number][]> = {
  view: [
    ["principle", "💡", "lobby", 1],
    ["gallery", "🖼", "lobby", 3],
    ["explore", "🧪", "sandbox", 4],
    ["reflect", "💬", "lobby", 2],
  ],
  challenge: [
    ["principle", "💡", "lobby", 1],
    ["pushout", "🥊", "pushout-ring", 5],
    ["gallery", "🖼", "lobby", 2],
    ["reflect", "💬", "lobby", 2],
  ],
  team: [
    ["principle", "💡", "lobby", 1],
    ["carry", "🤝", "carry-together", 6],
    ["reflect", "💬", "lobby", 3],
  ],
};

/** 시간 비례: 단계 초 = round(분 × 60 × 예산 / 600) · 최소 30(계약 §2.3) */
export function defaultTemplates(timeBudgetS = 600): ScriptTemplate[] {
  return SCRIPT_TEMPLATE_KEYS.map((key) => ({
    key,
    steps: BASE[key].map(([k, icon, modeId, min]) => ({
      key: k,
      icon,
      modeId,
      seconds: Math.max(30, Math.round((min * 60 * timeBudgetS) / 600)),
    })),
  }));
}

/** m:ss(0 아래는 0:00) */
export const mmss = (s: number) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

/** 분(반올림 · 30초 단계는 1분으로) */
export const minutesOf = (s: number) => Math.max(1, Math.round(s / 60));
